import { ApiCostEstimate } from '@/api/services/costEstimateService';

/** Owner + contractor fiscal-year amount for an operation line item (matches CostSummarySidebar). */
const operationFyAmount = (
  costEstimate: ApiCostEstimate,
  category: string,
): number => {
  const li = costEstimate.lineItems.find(
    (l) => l.section === 'operation' && l.category === category,
  );
  if (!li) return 0;
  return (
    (parseFloat(li.ownerAmountFiscalyear ?? '') || 0) +
    (parseFloat(li.contractorAmountFiscalyear ?? '') || 0)
  );
};

/**
 * Non-energy operating wedges for the project "Annual Costs" pie:
 *  - Personnel and Water Utility (fiscal-year owner + contractor).
 *  - Minor Maintenance shown only as the delta over the Conventional build's
 *    equipment maintenance (Jan, 2026-07-16): the computed equipment maintenance
 *    replaces the PIF minor_maintenance line; only the excess is added, and the
 *    same delta is applied to every build (Conv/LC/NZ).
 *  - energy_utility is intentionally dropped (replaced by the computed energy).
 * Zero-value wedges are omitted.
 */
export function computeOperatingWedges(
  costEstimate: ApiCostEstimate | undefined,
  conventionalEquipMaintenance: number,
): { label: string; value: number }[] {
  if (!costEstimate) return [];

  const wedges: { label: string; value: number }[] = [];

  const personnel = operationFyAmount(costEstimate, 'personnel');
  if (personnel > 0) wedges.push({ label: 'Personnel', value: personnel });

  const water = operationFyAmount(costEstimate, 'water_utility');
  if (water > 0) wedges.push({ label: 'Water Utility', value: water });

  const minorMaintenance = operationFyAmount(costEstimate, 'minor_maintenance');
  const delta = Math.max(0, minorMaintenance - conventionalEquipMaintenance);
  if (delta > 0) wedges.push({ label: 'Minor Maintenance', value: delta });

  return wedges;
}
